// UserProfile.js - Therapeut Profiel Pagina
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import './UserProfile.css';

const UserProfile = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    naam: '',
    telefoon: '',
    email: '',
    bedrijfsnaam: '',
    logoUrl: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [logoPreview, setLogoPreview] = useState('');

  // Laad bestaand profiel bij het openen van de pagina
  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const profileDoc = await getDoc(doc(db, 'userProfiles', user.uid));

      if (profileDoc.exists()) {
        const data = profileDoc.data();
        setFormData({
          naam: data.naam || '',
          telefoon: data.telefoon || '',
          email: data.email || user.email || '',
          bedrijfsnaam: data.bedrijfsnaam || '',
          logoUrl: data.logoUrl || ''
        });
        if (data.logoUrl) {
          setLogoPreview(data.logoUrl);
        }
      } else {
        // Nieuw profiel: vul email alvast in
        setFormData(prev => ({ ...prev, email: user.email || '' }));
      }
    } catch (error) {
      console.error('Fout bij laden profiel:', error);
      setErrorMessage('Profiel kon niet worden geladen.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLogoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Valideer bestandstype
    if (!file.type.startsWith('image/')) {
      setErrorMessage('Alleen afbeeldingen zijn toegestaan (jpg, png, svg).');
      return;
    }

    // Valideer bestandsgrootte (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setErrorMessage('Het logo mag maximaal 2MB zijn.');
      return;
    }

    // Toon preview
    const reader = new FileReader();
    reader.onload = (e) => setLogoPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload naar Firebase Storage
    try {
      setUploading(true);
      setErrorMessage('');
      const storageRef = ref(storage, `logos/${user.uid}/logo`);
      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);
      setFormData(prev => ({ ...prev, logoUrl: downloadUrl }));
    } catch (error) {
      console.error('Fout bij uploaden logo:', error);
      setErrorMessage('Logo kon niet worden geüpload. Probeer opnieuw.');
      setLogoPreview('');
    } finally {
      setUploading(false);
    }
  };

  const handleLogoRemove = () => {
    setLogoPreview('');
    setFormData(prev => ({ ...prev, logoUrl: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    // Validatie
    if (!formData.naam.trim()) {
      setErrorMessage('Naam is verplicht.');
      return;
    }

    try {
      setSaving(true);
      await setDoc(doc(db, 'userProfiles', user.uid), {
        ...formData,
        email: user.email, // altijd het accountemailadres gebruiken
        updatedAt: new Date()
      });
      setSuccessMessage('Profiel opgeslagen.');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Fout bij opslaan profiel:', error);
      setErrorMessage('Profiel kon niet worden opgeslagen. Probeer opnieuw.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-container">
        <div className="profile-loading">
          <div className="profile-spinner"></div>
          <p>Profiel laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-container">
      <div className="profile-wrapper">

        {/* Header */}
        <div className="profile-header">
          <div className="profile-header-content">
            <button
              className="profile-back-button"
              onClick={() => navigate('/dashboard')}
            >
              ← Terug naar dashboard
            </button>
            <h1>Mijn Profiel</h1>
            <p>Deze gegevens verschijnen op de laatste pagina van elk rapport.</p>
          </div>
        </div>

        {/* Formulier */}
        <div className="profile-card">
          <form onSubmit={handleSave}>

            {/* Feedbackmeldingen */}
            {successMessage && (
              <div className="profile-message profile-message-success">
                ✅ {successMessage}
              </div>
            )}
            {errorMessage && (
              <div className="profile-message profile-message-error">
                ⚠️ {errorMessage}
              </div>
            )}

            {/* Logo upload */}
            <div className="profile-field">
              <label className="profile-label">Logo</label>
              <div className="profile-logo-area">
                {logoPreview ? (
                  <div className="profile-logo-preview">
                    <img src={logoPreview} alt="Logo preview" className="profile-logo-img" />
                    <button
                      type="button"
                      className="profile-logo-remove"
                      onClick={handleLogoRemove}
                    >
                      Verwijderen
                    </button>
                  </div>
                ) : (
                  <div
                    className="profile-logo-placeholder"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <span className="profile-logo-icon">🖼️</span>
                    <span>Klik om een logo te uploaden</span>
                    <span className="profile-logo-hint">JPG, PNG of SVG • max 2MB</span>
                  </div>
                )}
                <input
                  type="file"
                  ref={fileInputRef}
                  accept="image/*"
                  onChange={handleLogoSelect}
                  className="profile-logo-input"
                />
                {uploading && (
                  <p className="profile-uploading">Logo wordt geüpload...</p>
                )}
              </div>
            </div>

            {/* Naam */}
            <div className="profile-field">
              <label className="profile-label" htmlFor="naam">
                Naam <span className="profile-required">*</span>
              </label>
              <input
                type="text"
                id="naam"
                name="naam"
                value={formData.naam}
                onChange={handleChange}
                className="profile-input"
                placeholder="Bijv. Diederik Kolb"
              />
            </div>

            {/* Bedrijfsnaam */}
            <div className="profile-field">
              <label className="profile-label" htmlFor="bedrijfsnaam">
                Bedrijfsnaam
              </label>
              <input
                type="text"
                id="bedrijfsnaam"
                name="bedrijfsnaam"
                value={formData.bedrijfsnaam}
                onChange={handleChange}
                className="profile-input"
                placeholder="Bijv. PABB VOF"
              />
            </div>

            {/* Telefoon */}
            <div className="profile-field">
              <label className="profile-label" htmlFor="telefoon">
                Telefoonnummer
              </label>
              <input
                type="tel"
                id="telefoon"
                name="telefoon"
                value={formData.telefoon}
                onChange={handleChange}
                className="profile-input"
                placeholder="Bijv. 06-12345678"
              />
            </div>

            {/* Email - alleen lezen */}
            <div className="profile-field">
              <label className="profile-label" htmlFor="email">
                E-mailadres
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={user.email || ''}
                className="profile-input profile-input-readonly"
                readOnly
              />
              <p className="profile-field-hint">
                Dit is het e-mailadres van je account en kan hier niet worden gewijzigd.
              </p>
            </div>

            {/* Opslaan knop */}
            <div className="profile-actions">
              <button
                type="submit"
                className="profile-save-button"
                disabled={saving || uploading}
              >
                {saving ? 'Opslaan...' : '💾 Profiel opslaan'}
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
